import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { PostId } from '../model/post.types';
import { Reply } from '../model/reply.types';
import { useDeleteReply } from '../api/useDeleteReply';
import { useBlockUser } from '../api/useBlockUser';
import { useReplyInDm } from '../lib/useReplyInDm';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { ActionConfirmModal } from './ActionConfirmModal';

type ConfirmKind = 'delete' | 'block' | null;

type Props = {
  reply: Reply;
  postId: PostId;
  onEdit?: () => void;
};

export const ReplyActionsMenu = ({ reply, postId, onEdit }: Props) => {
  const { communityId, canModerate, isDmFeatureEnabled } =
    useCommunityContext();
  const deleteReply = useDeleteReply();
  const blockUser = useBlockUser();
  const [confirming, setConfirming] = useState<ConfirmKind>(null);

  const { id: authorId, email, canReceiveDm, isOwn, isCreator } = reply.author;
  const replyInDM = useReplyInDm(authorId);

  const dismiss = useCallback(() => setConfirming(null), []);

  const copyEmail = useCallback(() => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    analytics.trackAuthorEmailCopied();
    toast.success('Email copied to clipboard!');
  }, [email]);

  const confirmDelete = useCallback(() => {
    analytics.trackCommentDeleted(reply);
    deleteReply({ communityId, postId, replyId: reply.id });
    setConfirming(null);
  }, [deleteReply, communityId, postId, reply]);

  const confirmBlock = useCallback(() => {
    analytics.trackUserBlocked('feed');
    blockUser({ communityId, userId: authorId });
    setConfirming(null);
  }, [blockUser, communityId, authorId]);

  const showBlock = canModerate && !isOwn && !isCreator;

  const items = useMemo<ActionMenuItem[]>(() => {
    const entries: Array<ActionMenuItem | null> = [
      onEdit ? { key: 'edit', label: 'Edit', onClick: onEdit } : null,
      {
        key: 'delete',
        label: 'Delete',
        destructive: true,
        onClick: () => setConfirming('delete'),
      },
      email && !isOwn
        ? { key: 'copy-email', label: 'Copy email', onClick: copyEmail }
        : null,
      isDmFeatureEnabled && canReceiveDm && !isOwn
        ? { key: 'reply-dm', label: 'Reply in DM', onClick: replyInDM }
        : null,
      showBlock
        ? {
            key: 'block',
            label: 'Block user',
            destructive: true,
            onClick: () => setConfirming('block'),
          }
        : null,
    ];
    return entries.filter((item): item is ActionMenuItem => item !== null);
  }, [
    email,
    isDmFeatureEnabled,
    canReceiveDm,
    isOwn,
    showBlock,
    onEdit,
    copyEmail,
    replyInDM,
  ]);

  return (
    <>
      <ActionMenu items={items} />
      <ActionConfirmModal
        open={confirming === 'delete'}
        title="Are you sure you want to delete this comment?"
        actionLabel="Delete"
        onConfirm={confirmDelete}
        onDismiss={dismiss}
      />
      <ActionConfirmModal
        open={confirming === 'block'}
        title="Are you sure you want to block this user?"
        body="This means that they will no longer be able to engage in your community."
        actionLabel="Block user"
        onConfirm={confirmBlock}
        onDismiss={dismiss}
      />
    </>
  );
};
